import React, { useCallback, useMemo, useState } from 'react'
import {
  Button,
  Divider,
  Table,
  Tag,
  Tooltip,
  message,
} from '@surecompanies/core_components'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import DownOutlined from '@ant-design/icons/DownOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import ReloadOutlined from '@ant-design/icons/ReloadOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { TableProps } from '@surecompanies/core_components'
import type { ColumnType } from 'antd/es/table/interface'
import { SURECO_CSV_DATA_TYPE_OPTIONS } from '../constants'
import {
  getHealthPlanUploadLogDownloadUrl,
  getHealthPlanUploadLogs,
  getHealthPlanUploaders,
  type HealthPlanUploadLogAdditionalDataDto,
  type HealthPlanUploadLogDto,
} from '../api/health-plan-api'
import styles from '../PlanManagementV3.module.scss'

function dataTypeLabel(
  dataType: HealthPlanUploadLogAdditionalDataDto['dataType']
): string {
  return (
    SURECO_CSV_DATA_TYPE_OPTIONS.find((o) => o.value === dataType)?.label ??
    dataType
  )
}

function escapeCsvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const UPLOAD_LOGS_CSV_HEADERS = [
  'Timestamp',
  'Data Type',
  'State',
  'Uploaded By',
  'Status',
  'Total Rows',
  'Inserted',
  'Updated',
  'Row Errors',
]

function buildUploadLogsCsv(rows: HealthPlanUploadLogDto[]): string {
  const lines = rows.map((row) => {
    const d = row.additionalData
    return [
      dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      dataTypeLabel(d.dataType),
      d.states?.join('; ') ?? '',
      row.actorName,
      d.success ? 'Success' : 'Failed',
      String(d.totalRows),
      String(d.inserted),
      String(d.updated),
      String(d.rowErrors),
    ]
      .map(escapeCsvField)
      .join(',')
  })
  return [UPLOAD_LOGS_CSV_HEADERS.join(','), ...lines].join('\n')
}

function S3DownloadButton({ s3Key }: { s3Key: string }) {
  const [isLoading, setIsLoading] = useState(false)
  const filename = s3Key.split('/').pop() ?? s3Key

  const handleDownload = useCallback(async () => {
    setIsLoading(true)
    try {
      const url = await getHealthPlanUploadLogDownloadUrl(s3Key)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to get download URL', err)
      message.error('Could not generate a download link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [s3Key])

  return (
    <Tooltip title={s3Key}>
      <Button
        type="link"
        size="small"
        icon={isLoading ? <LoadingOutlined /> : <DownloadOutlined />}
        disabled={isLoading}
        onClick={() => void handleDownload()}
        style={{ padding: 0, height: 'auto', fontSize: 12 }}
      >
        <span
          style={{
            fontFamily: 'monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {filename}
        </span>
      </Button>
    </Tooltip>
  )
}

interface DetailRow {
  type: string
  kind: 'file' | 'count'
  s3Key?: string | null
  value?: number
}

function buildDetailRows(d: HealthPlanUploadLogAdditionalDataDto): DetailRow[] {
  if (!d.success) {
    return [
      { type: 'Uploaded file', kind: 'file', s3Key: d.s3.originalFile },
      { type: 'Row errors', kind: 'file', s3Key: d.s3.errors },
    ]
  }
  return [
    { type: 'Uploaded file', kind: 'file', s3Key: d.s3.originalFile },
    { type: 'Diff (Inserted/Changed Rows)', kind: 'file', s3Key: d.s3.diff },
    {
      type: 'Original Data (Before Update)',
      kind: 'file',
      s3Key: d.s3.originalValues,
    },
    { type: 'Generated SQL', kind: 'file', s3Key: d.s3.writeSql },
    { type: 'Rollback SQL', kind: 'file', s3Key: d.s3.rollback },
    { type: 'Inserted', kind: 'count', value: d.inserted },
    { type: 'Updated', kind: 'count', value: d.updated },
  ]
}

function renderLogDetails(record: HealthPlanUploadLogDto) {
  const d = record.additionalData
  const rows = buildDetailRows(d)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {d.errorMessage && (
        <span style={{ fontSize: 12, color: '#cf1322' }}>
          <strong>Error:</strong> {d.errorMessage}
        </span>
      )}
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                padding: '6px 8px',
                borderBottom: '1px solid #e5e7eb',
                color: '#374151',
                fontWeight: 600,
              }}
            >
              Type
            </th>
            <th style={{ borderBottom: '1px solid #e5e7eb' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.type}>
              <td
                style={{
                  padding: '6px 8px',
                  borderBottom: '1px solid #f3f4f6',
                  color: '#374151',
                }}
              >
                {row.type}
              </td>
              <td
                style={{
                  padding: '6px 8px',
                  borderBottom: '1px solid #f3f4f6',
                  textAlign: 'right',
                }}
              >
                {row.kind === 'count' ? (
                  row.value
                ) : row.s3Key ? (
                  <S3DownloadButton s3Key={row.s3Key} />
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function HealthPlanUploadLogTable() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null)
  const [selectedActorIds, setSelectedActorIds] = useState<string[]>([])
  const [isExportingLogs, setIsExportingLogs] = useState(false)

  const { data: uploaders } = useQuery({
    queryKey: ['healthPlanUploaders'],
    queryFn: getHealthPlanUploaders,
  })

  const {
    data: logsData,
    isLoading: isLoadingLogs,
    isFetching: isFetchingLogs,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['healthPlanUploadLogs', page, pageSize, selectedActorIds],
    queryFn: () =>
      getHealthPlanUploadLogs({
        page,
        limit: pageSize,
        actorIds: selectedActorIds,
      }),
    placeholderData: (previousData) => previousData,
  })

  const handleExportLogsCsv = useCallback(async () => {
    setIsExportingLogs(true)
    try {
      const { rows } = await getHealthPlanUploadLogs({
        page: 1,
        limit: 5000,
        actorIds: selectedActorIds,
      })
      const csv = buildUploadLogsCsv(rows)
      const blob = new Blob(['﻿', csv], {
        type: 'text/csv;charset=utf-8;',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `health-plan-upload-logs-${dayjs().format('YYYYMMDD-HHmmss')}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to export upload logs csv', err)
      message.error('Export failed. Please try again.')
    } finally {
      setIsExportingLogs(false)
    }
  }, [selectedActorIds])

  const uploaderFilterOptions = useMemo(
    () => (uploaders ?? []).map((u) => ({ text: u.name, value: u.id })),
    [uploaders]
  )

  const toggleExpanded = useCallback((id: number) => {
    setExpandedLogId((prev) => (prev === id ? null : id))
  }, [])

  const columns = useMemo<ColumnType<HealthPlanUploadLogDto>[]>(
    () => [
      {
        title: 'Data Type',
        key: 'dataType',
        width: 220,
        render: (_: unknown, record: HealthPlanUploadLogDto) => {
          const isExpanded = expandedLogId === record.id
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
              onClick={() => toggleExpanded(record.id)}
            >
              {isExpanded ? (
                <DownOutlined style={{ fontSize: 10, color: '#6b7280' }} />
              ) : (
                <RightOutlined style={{ fontSize: 10, color: '#6b7280' }} />
              )}
              {dataTypeLabel(record.additionalData.dataType)}
            </span>
          )
        },
      },
      {
        title: 'State',
        key: 'state',
        width: 120,
        render: (_: unknown, record: HealthPlanUploadLogDto) => {
          const stateDisplay = record.additionalData.states?.join(', ') ?? '—'
          const isOverFlow = stateDisplay.length > 15

          return (
            <Tooltip title={isOverFlow ? stateDisplay : ''}>
              <span
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {stateDisplay}
              </span>
            </Tooltip>
          )
        },
      },
      {
        title: 'Uploaded By',
        dataIndex: 'actorName',
        key: 'actorId',
        width: 160,
        ellipsis: true,
        filters: uploaderFilterOptions,
        filterSearch: true,
        filteredValue: selectedActorIds.length ? selectedActorIds : null,
      },
      {
        title: 'Timestamp',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 160,
        render: (timestamp: string) =>
          dayjs(timestamp).format('MMM D, YYYY h:mm A'),
      },
      {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_: unknown, record: HealthPlanUploadLogDto) =>
          record.additionalData.success ? (
            <Tag color="green">Success</Tag>
          ) : (
            <Tag color="red">Failed</Tag>
          ),
      },
    ],
    [uploaderFilterOptions, selectedActorIds, expandedLogId, toggleExpanded]
  )

  const handleTableChange: TableProps<HealthPlanUploadLogDto>['onChange'] =
    useCallback((_pagination, filters, _sorter, extra) => {
      if (extra.action !== 'filter') return
      const actorIds = (filters.actorId as string[] | null) ?? []
      setSelectedActorIds(actorIds)
      setPage(1)
    }, [])

  return (
    <div className={styles.card}>
      <Divider style={{ marginTop: 0, borderWidth: '2px' }} />
      <div className={styles.cardHeader}>
        <h1
          className={styles.cardHeaderTitle}
          style={{
            fontSize: '18px',
          }}
        >
          Upload Logs
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => void handleExportLogsCsv()}
            loading={isExportingLogs}
          >
            Export CSV
          </Button>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void refetchLogs()}
            loading={isFetchingLogs}
          >
            Refresh
          </Button>
        </div>
      </div>
      <Table<HealthPlanUploadLogDto>
        className={styles.flatTable}
        rowKey="id"
        size="small"
        loading={isLoadingLogs}
        dataSource={logsData?.rows ?? []}
        columns={columns}
        onChange={handleTableChange}
        scroll={{ x: 760 }}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: expandedLogId != null ? [expandedLogId] : [],
          expandedRowRender: renderLogDetails,
        }}
        pagination={{
          current: page,
          pageSize,
          total: logsData?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          size: 'small',
          onChange: (p, ps) => {
            setPage(p)
            setPageSize(ps)
          },
        }}
        locale={{ emptyText: 'No uploads yet.' }}
      />
    </div>
  )
}
